import { endOfDay } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../app/AuthorizationProvider';
import { DropdownTimezone, DropdownTimezoneItem, DropdownTimezoneSubmenu } from '../../../components/DropdownTimezone';
import { useGetTimezonesAllQuery } from '../../../graphql/components';
import { getUTCHoursOffsetLabel } from '../../../utils/date';
import { getPrevalentOffsetDateFromInterval } from '../../../utils/timezone';
import { formatTimezoneIdToName } from '../../../utils/timezoneFormatter';

type Props = {
  startDate: Date;
  endDate: Date;
  queueTimezone: string;
  setTimezone: (timezone: string) => void;
  timezone: string;
};

export const ForecastingDropdownTimezone: React.FC<Props> = ({
  startDate,
  endDate,
  timezone,
  queueTimezone,
  setTimezone,
}) => {
  const { locale, timezone: userTimezone } = useAuth();
  const { t } = useTranslation();

  const { data } = useGetTimezonesAllQuery();

  const timezones = (data?.timezones ?? []).map((tz) => tz.zoneId);
  const date = endOfDay(getPrevalentOffsetDateFromInterval(startDate, endDate, timezone));

  return (
    <div className="d-flex align-items-center">
      <div className="text-right text-grey-600 mr-2">
        {formatTimezoneIdToName(timezone)}
        <br />
        <strong>{getUTCHoursOffsetLabel(date, timezone, locale, t)}</strong>
      </div>
      <DropdownTimezone activeTimezone={timezone} alignment="right" onTimezoneChange={setTimezone}>
        <DropdownTimezoneItem
          subtitle={`${formatTimezoneIdToName(userTimezone)} ${getUTCHoursOffsetLabel(date, userTimezone, locale, t)}`}
          tz={userTimezone}
        >
          {t('timezone.myTimezone')}
        </DropdownTimezoneItem>
        <DropdownTimezoneItem
          subtitle={`${formatTimezoneIdToName(queueTimezone)} ${getUTCHoursOffsetLabel(
            date,
            queueTimezone,
            locale,
            t
          )}`}
          tz={queueTimezone}
        >
          {t('timezone.queueTimezone')}
        </DropdownTimezoneItem>
        <DropdownTimezoneSubmenu items={timezones} showSearch>
          <DropdownTimezoneItem subtitle={t('timezone.allAvailableTimezones')}>
            {t('timezone.allTimezones')}
          </DropdownTimezoneItem>
        </DropdownTimezoneSubmenu>
      </DropdownTimezone>
    </div>
  );
};
